import React, { useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { categories } from 'constants/tests';
import services from 'api/services';
import { useFreecodeContextData } from 'hooks/useContext';
import { FreecodeCheckbox } from 'components/Checkbox/Checkbox';
import { FreecodeButton } from 'components/Button/RectangleButton';
import { ButtonIcon as RectangleButtonIcon } from 'components/Button/styles';
import {
  CreatorTabContentBackground,
  CreatorTabRowContainer,
  CreatorTabColumnContainer,
  CreatorSection,
  NumberInput,
  SectionHeader,
  Label,
  Text,
  InputContainer,
  HiddenFileInput,
} from '../styles';
import { RoundIconButton } from 'components/Button/IconButton';
import BackgroundColorPicker from './BackgroundColorPicker';
import GraphicsDataModal from './GraphicsDataModal';
import GraphicsDeleteModal from './GraphicsDeleteModal';
import addFreecodeGraphics from 'components/FreecodePlayer/addFreecodeGraphics';
import {
  LandingPageImage,
  GraphicsImagesContainer,
  GraphicsImageRow,
  GraphicsImage,
  GraphicsImageCell,
} from './styles';

const { FreecodeCreatorService } = services;

export default function FreecodeGraphics() {
  const { test, testImpl, setTest, setTestImpl } = useFreecodeContextData();

  const [isUploadingGraphics, setIsUploadingGraphics] = useState(false);
  const [dataModalImageIndex, setDataModalImageIndex] = useState(-1);
  const [deleteModalImageIndex, setDeleteModalImageIndex] = useState(-1);

  const hiddenFileInputForImage = useRef();
  const hiddenFileInputForBackground = useRef();

  const handleConfigCanvasUpdate = (parameter, value) =>
    setTestImpl((oldTestImpl) => ({
      ...oldTestImpl,
      configCanvas: { ...oldTestImpl.configCanvas, [parameter]: value },
    }));

  const handleConfigCanvasNumberUpdate = (parameter, value) =>
    !isNaN(value) && handleConfigCanvasUpdate(parameter, value);

  const handleFileUpload = (event, isBackgroundImage = false) => {
    let file = event?.target?.files?.[0];
    if (!file || !file.type.includes('image')) return toast.error('Only image file can be uploaded');
    if (isUploadingGraphics) return toast.error('Already uploading');
    setIsUploadingGraphics(true);

    const doUpload = async () => {
      try {
        await new Promise((resolve, reject) => {
          try {
            const reader = new FileReader();

            reader.onload = async () => {
              try {
                // Verify name not taken
                if (testImpl.graphics.some((graphic) => graphic.name === file.name)) {
                  return reject('Image with that name already exists');
                }

                // Verify image not to large
                await new Promise(function (resolve, reject) {
                  let img = new Image();
                  img.onload = function () {
                    if (img.width > 4096) reject('Image width over 4096px');
                    if (img.height > 4096) reject('Image height over 4096px');
                    resolve();
                  };
                  img.onerror = reject;
                  img.crossOrigin = 'anonymous';
                  img.src = reader.result;
                });

                // Upload the image
                let response = await FreecodeCreatorService.saveNewGraphics(test._id, testImpl?._id, {
                  data: reader.result,
                  uploadType: isBackgroundImage ? 'background' : 'graphicsImage',
                  imageName: file.name,
                  isBackgroundImage: isBackgroundImage,
                });
                if (response.error) return reject(response.error.message || 'Upload returned an error');

                if (isBackgroundImage) {
                  if (!response.backgroundImage) return reject('Response missing new background');
                  setTest((oldTest) => ({ ...oldTest, backgroundImage: response.backgroundImage }));
                  return resolve();
                }

                if (!response.graphics) return reject('Response missing new graphics');
                setTestImpl((oldTestImpl) => ({ ...oldTestImpl, graphics: response.graphics }));

                // Find new image url and add it to canvas
                let graphicData = response.graphics.find((graphic) => graphic.name === file.name);
                if (!graphicData?.url) return reject('Could not find image to add into canvas');
                await addFreecodeGraphics(graphicData);

                resolve();
              } catch (e) {
                reject(e);
              }
            };

            reader.onerror = () => reject('Could Not Read File');
            reader.readAsDataURL(file);
          } catch (e) {
            reject(e);
          }
        });
        toast.success('Image has been uploaded!');
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not upload graphics');
      } finally {
        if (event?.target) event.target.value = null;
        setIsUploadingGraphics(false);
      }
    };
    doUpload();
  };

  const handleCheckboxChange = (option, index) => {
    let configCanvas = { ...testImpl.configCanvas };

    if (option === 'interactionByLevel') {
      configCanvas.interactionByLevel[index] = !configCanvas.interactionByLevel[index];
    } else if (configCanvas[option]) {
      switch (option) {
        case 'canvasUsed':
          configCanvas.canvasUsed = false;
        // falls through
        case 'canvasAnimated':
          configCanvas.canvasAnimated = false;
        // falls through
        case 'canvasHumanInput':
          configCanvas.canvasHumanInput = false;
          configCanvas.canvasHumanAdapter = false;
          configCanvas.humanInputOwnStepTime = false;
          delete configCanvas.interactionByLevel;
          break;
        default:
          configCanvas[option] = false;
      }
    } else {
      switch (option) {
        case 'humanInputOwnStepTime':
        // falls through
        case 'canvasHumanAdapter':
          configCanvas[option] = true;
        // falls through
        case 'canvasHumanInput':
          configCanvas.canvasHumanInput = true;
          configCanvas.interactionByLevel = new Array(
            testImpl.configLevels.numPublic + testImpl.configLevels.numHidden
          ).fill(true);
        // falls through
        case 'canvasAnimated':
          configCanvas.canvasAnimated = true;
        // falls through
        default:
          configCanvas.canvasUsed = true;
      }
    }

    setTestImpl((oldTestImpl) => ({ ...oldTestImpl, configCanvas }));
  };

  const renderGraphicsImages = () => {
    if (testImpl.graphics?.length > 0)
      return (
        <GraphicsImagesContainer>
          {testImpl.graphics.map((image, index) => (
            <GraphicsImageRow key={`${index}${image.name}`}>
              <GraphicsImageCell>
                <GraphicsImage src={image.url} />
              </GraphicsImageCell>
              <GraphicsImageCell overflowHidden>
                <Label hideOverflow title={image.name}>
                  {image.name}
                </Label>
              </GraphicsImageCell>
              <GraphicsImageCell push>
                <RoundIconButton
                  isAPIButton
                  icon="fa-regular fa-file-code"
                  onClick={() => setDataModalImageIndex(index)}
                />
              </GraphicsImageCell>
              <GraphicsImageCell>
                <RoundIconButton isAPIButton icon="fa-solid fa-trash" onClick={() => setDeleteModalImageIndex(index)} />
              </GraphicsImageCell>
            </GraphicsImageRow>
          ))}
        </GraphicsImagesContainer>
      );
  };

  return (
    <CreatorTabContentBackground>
      <CreatorTabRowContainer>
        <CreatorTabColumnContainer>
          <CreatorSection>
            <SectionHeader>Canvas Animation</SectionHeader>
            <FreecodeCheckbox
              checked={testImpl.configCanvas.canvasUsed}
              label="Uses Canvas"
              onChange={() => handleCheckboxChange('canvasUsed')}
            />
            <FreecodeCheckbox
              checked={testImpl.configCanvas.canvasAnimated}
              label="Has Canvas Animation"
              onChange={() => handleCheckboxChange('canvasAnimated')}
            />
            <FreecodeCheckbox
              checked={testImpl.configCanvas.canvasHumanInput}
              label="Supports Human Input"
              onChange={() => handleCheckboxChange('canvasHumanInput')}
            />
            {testImpl.configCanvas.canvasHumanInput &&
              testImpl.configCanvas?.interactionByLevel?.map((_, index) => {
                return (
                  <FreecodeCheckbox
                    key={`interactionByLevel${index}`}
                    checked={testImpl.configCanvas.interactionByLevel[index]}
                    label={`Level ${index + 1}`}
                    usePadding="0 0 0 26px"
                    onChange={() => handleCheckboxChange('interactionByLevel', index)}
                  />
                );
              })}
            <FreecodeCheckbox
              checked={testImpl.configCanvas.canvasHumanAdapter}
              label="Human Input using HumanAdapter"
              onChange={() => handleCheckboxChange('canvasHumanAdapter')}
            />
            <FreecodeCheckbox
              checked={testImpl.configCanvas.humanInputOwnStepTime}
              label="Human Input using separate step time"
              onChange={() => handleCheckboxChange('humanInputOwnStepTime')}
            />
            {testImpl.configCanvas.canvasAnimated && (
              <InputContainer>
                <Label noWrap>Canvas step time (ms):</Label>
                <NumberInput
                  value={testImpl.configCanvas.stepTime}
                  onChange={(e) => handleConfigCanvasNumberUpdate('stepTime', e.target.value)}
                ></NumberInput>
              </InputContainer>
            )}
            {testImpl.configCanvas.humanInputOwnStepTime && (
              <InputContainer>
                <Label noWrap>Human Input step time (ms):</Label>
                <NumberInput
                  value={testImpl.configCanvas.stepTimeHumanInput}
                  onChange={(e) => handleConfigCanvasNumberUpdate('stepTimeHumanInput', e.target.value)}
                ></NumberInput>
              </InputContainer>
            )}
          </CreatorSection>
          {testImpl.configCanvas.canvasUsed && (
            <CreatorSection>
              <SectionHeader>Images</SectionHeader>
              <Text>Images that can be used in the canvas</Text>
              <FreecodeButton
                small
                marginTop
                isFreecodeButton
                title="Upload an image that can be used in the canvas"
                onClick={() => hiddenFileInputForImage.current.click()}
                disabled={isUploadingGraphics}
              >
                <RectangleButtonIcon icon="fa fa-plus" />
                Upload
              </FreecodeButton>
              <HiddenFileInput ref={hiddenFileInputForImage} onChange={handleFileUpload}></HiddenFileInput>
              {renderGraphicsImages()}
            </CreatorSection>
          )}
        </CreatorTabColumnContainer>
        <CreatorTabColumnContainer>
          {testImpl.configCanvas.canvasUsed && (
            <>
              <BackgroundColorPicker
                background={testImpl.configCanvas?.background}
                setBackground={(color) => handleConfigCanvasUpdate('background', color)}
              />
              <CreatorSection>
                <SectionHeader>Resolution</SectionHeader>
                <Text>Canvas aspect ratio and index for graphical element placement</Text>
                <InputContainer>
                  <Label>Width:</Label>
                  <NumberInput
                    value={testImpl.configCanvas.width}
                    onChange={(e) => handleConfigCanvasNumberUpdate('width', e.target.value)}
                  ></NumberInput>
                  <Label>Height:</Label>
                  <NumberInput
                    value={testImpl.configCanvas.height}
                    onChange={(e) => handleConfigCanvasNumberUpdate('height', e.target.value)}
                  ></NumberInput>
                </InputContainer>
              </CreatorSection>
            </>
          )}
          {test.category !== categories.CODE.category && (
            <CreatorSection>
              <SectionHeader>Landing Page Image</SectionHeader>
              <Text>Default landing page background and thumbnail</Text>
              <FreecodeButton
                small
                isFreecodeButton
                marginTop
                title="Upload an image that will replace the background and thumbnail"
                onClick={() => hiddenFileInputForBackground.current.click()}
                disabled={isUploadingGraphics}
              >
                <RectangleButtonIcon icon="fa fa-plus" />
                Upload
              </FreecodeButton>
              <HiddenFileInput
                ref={hiddenFileInputForBackground}
                onChange={(e) => handleFileUpload(e, true)}
              ></HiddenFileInput>
              <LandingPageImage src={test.backgroundImage?.url || test.backgroundImage?.thumbnail}></LandingPageImage>
            </CreatorSection>
          )}
        </CreatorTabColumnContainer>
      </CreatorTabRowContainer>
      {dataModalImageIndex > -1 && (
        <GraphicsDataModal graphicsIndex={dataModalImageIndex} closeModal={() => setDataModalImageIndex(-1)} />
      )}
      {deleteModalImageIndex > -1 && (
        <GraphicsDeleteModal graphicsIndex={deleteModalImageIndex} closeModal={() => setDeleteModalImageIndex(-1)} />
      )}
    </CreatorTabContentBackground>
  );
}
